import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import CloseIcon from "@mui/icons-material/Close";
import Slider from '@mui/material/Slider';
import TextField from '@mui/material/TextField';
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import { get_t_str, get_t_str_shortened } from '../../../../helpers/helpers';

function ManualIntradayDistributionModal(props) {
  // Generate half-hour time slots based on hours of operation
  const generateTimeSlots = () => {
    let openTime, closeTime;

    console.log('=== ManualIntradayDistributionModal Props ===');
    console.log('props.hoo:', props.hoo);
    console.log('props.manualForecastTotal:', props.manualForecastTotal);

    // Check if we have hoo object with open_time and close_time
    if (props.hoo && typeof props.hoo.open_time === 'number' && typeof props.hoo.close_time === 'number') {
      // The hoo object contains decimal hours (e.g., 9.5 = 9:30am)
      openTime = props.hoo.open_time;
      closeTime = props.hoo.close_time;
      console.log('Using props.hoo times (decimal hours):', { openTime, closeTime });
    } else {
      // Default to 9am - 9pm if no hours provided
      openTime = 9.0;
      closeTime = 21.0;
      console.log('Using default times:', { openTime, closeTime });
    }

    console.log('Times formatted:', {
      openFormatted: get_t_str(openTime),
      closeFormatted: get_t_str(closeTime)
    });

    const slots = [];
    let iterationCount = 0;

    // Generate 30-minute intervals (0.5 hour increments)
    for (let time = openTime; time < closeTime; time += 0.5) {
      iterationCount++;
      const endTime = Math.min(time + 0.5, closeTime);
      const slot = {
        startTime: time,
        endTime: endTime,
        startLabel: get_t_str(time),
        endLabel: get_t_str(endTime)
      };
      console.log(`Slot ${iterationCount}:`, slot);
      slots.push(slot);

      // Safety check to prevent infinite loop
      if (iterationCount > 100) {
        console.error('Too many iterations! Breaking loop.');
        break;
      }
    }

    console.log(`Total slots generated: ${slots.length}`);
    console.log('All slots:', slots);
    return slots;
  };

  const timeSlots = generateTimeSlots();
  console.log('timeSlots array length:', timeSlots.length);

  // Initialize distribution with mock data (evenly distributed for now)
  const initializeDistribution = () => {
    const evenPercentage = (100 / timeSlots.length).toFixed(1);
    const distribution = {};

    timeSlots.forEach((slot, index) => {
      if (index === timeSlots.length - 1) {
        // Make sure the last one accounts for rounding to total 100%
        const currentTotal = Object.values(distribution).reduce((sum, val) => sum + parseFloat(val), 0);
        distribution[slot.startTime] = (100 - currentTotal).toFixed(1);
      } else {
        distribution[slot.startTime] = evenPercentage;
      }
    });

    return distribution;
  };

  const [distribution, setDistribution] = useState(initializeDistribution());
  const [savingLoading, setSavingLoading] = useState(false);
  const [manualTotal, setManualTotal] = useState(props.manualForecastTotal || 0);
  const [loadingDistribution, setLoadingDistribution] = useState(false);

  useEffect(() => {
    setManualTotal(props.manualForecastTotal || 0);
  }, [props.manualForecastTotal]);

  const applyDistributionFromApi = (apiDistribution) => {
    if (!apiDistribution || apiDistribution.length === 0) {
      setDistribution(initializeDistribution());
      return;
    }

    const mappedDistribution = {};
    timeSlots.forEach((slot) => {
      mappedDistribution[slot.startTime] = '0.0';
    });

    apiDistribution.forEach((slot) => {
      const startTime = parseFloat(slot.qh_num);
      mappedDistribution[startTime] = parseFloat(slot.percentage || 0).toFixed(1);
    });

    setDistribution(mappedDistribution);
  };

  useEffect(() => {
    if (!props.open) {
      return;
    }

    if (!props.fetchDistribution || !props.dateStr) {
      setDistribution(initializeDistribution());
      return;
    }

    setLoadingDistribution(true);
    props
      .fetchDistribution(props.dateStr)
      .then((response) => {
        const apiDistribution = response?.data?.distribution || [];
        applyDistributionFromApi(apiDistribution);

        if (response?.data?.sales_value !== undefined) {
          setManualTotal(parseFloat(response.data.sales_value) || 0);
        }
      })
      .catch((err) => {
        console.error('Error fetching intraday distribution', err);
        setDistribution(initializeDistribution());
      })
      .finally(() => {
        setLoadingDistribution(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.dateStr, timeSlots.length]);
  const [editingSlot, setEditingSlot] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Calculate total percentage
  const totalPercentage = Object.values(distribution).reduce(
    (sum, val) => sum + parseFloat(val || 0),
    0
  ).toFixed(1);

  const isValidTotal = parseFloat(totalPercentage) === 100.0;

  // Calculate max slider value based on number of slots
  // Use 5x the even distribution (100 / number of slots), rounded to nearest 5
  const calculateMaxSliderValue = () => {
    const evenDistribution = 100 / timeSlots.length;
    const maxValue = evenDistribution * 5;
    const roundedTo5 = Math.ceil(maxValue / 5) * 5;
    return Math.max(15, roundedTo5);
  };
  const maxSliderValue = calculateMaxSliderValue();

  // Handle slider change
  const handleSliderChange = (startTime, newValue) => {
    // Clamp value between 0 and 100
    const clampedValue = Math.min(100, Math.max(0, newValue));
    setDistribution(prev => ({
      ...prev,
      [startTime]: clampedValue.toFixed(1)
    }));
  };

  // Handle text input change
  const handleInputChange = (startTime, value) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      // Clamp to maximum of 100%
      const clampedValue = Math.min(100, numValue);
      setDistribution(prev => ({
        ...prev,
        [startTime]: clampedValue.toFixed(1)
      }));
    }
  };

  // Handle clicking on a slot to edit
  const handleSlotClick = (startTime) => {
    setEditingSlot(startTime);
    setEditValue(distribution[startTime] || '0');
  };

  // Handle saving the edited value
  const handleEditSave = () => {
    if (editingSlot !== null) {
      handleInputChange(editingSlot, editValue);
      setEditingSlot(null);
      setEditValue('');
    }
  };

  // Handle cancel editing
  const handleEditCancel = () => {
    setEditingSlot(null);
    setEditValue('');
  };

  // Auto-adjust remaining slots to make total = 100%
  const handleAutoAdjust = () => {
    const currentTotal = parseFloat(totalPercentage);
    const difference = 100 - currentTotal;

    if (difference === 0) return; // Already at exactly 100%

    // Convert difference to increments of 0.1 (to avoid floating point errors)
    const incrementsNeeded = Math.round(difference * 10);
    const singleIncrement = incrementsNeeded > 0 ? 0.1 : -0.1;

    // Distribute increments evenly across slots
    const incrementsPerSlot = Math.floor(Math.abs(incrementsNeeded) / timeSlots.length);
    const remainingIncrements = Math.abs(incrementsNeeded) % timeSlots.length;

    const newDistribution = {};
    timeSlots.forEach((slot, index) => {
      const currentValue = parseFloat(distribution[slot.startTime]);

      // Each slot gets base increments
      let totalIncrements = incrementsPerSlot;

      // First N slots get one extra increment to distribute remainder
      if (index < remainingIncrements) {
        totalIncrements += 1;
      }

      // Apply adjustment
      const adjustment = totalIncrements * singleIncrement;
      const newValue = currentValue + adjustment;

      newDistribution[slot.startTime] = Math.max(0, Math.min(100, newValue)).toFixed(1);
    });

    // Verify total is exactly 100.0, if not, adjust the last non-zero slot
    let newTotal = Object.values(newDistribution).reduce((sum, val) => sum + parseFloat(val), 0);
    newTotal = parseFloat(newTotal.toFixed(1));

    if (newTotal !== 100.0) {
      const finalDifference = parseFloat((100.0 - newTotal).toFixed(1));

      // Find last slot with enough value to adjust (or first slot if adding)
      let adjustedSlot = null;
      for (let i = timeSlots.length - 1; i >= 0; i--) {
        const slotTime = timeSlots[i].startTime;
        const currentVal = parseFloat(newDistribution[slotTime]);

        if (finalDifference > 0 || (finalDifference < 0 && currentVal >= Math.abs(finalDifference))) {
          adjustedSlot = slotTime;
          break;
        }
      }

      // If we couldn't find a suitable slot, use the first one
      if (adjustedSlot === null) {
        adjustedSlot = timeSlots[0].startTime;
      }

      const currentVal = parseFloat(newDistribution[adjustedSlot]);
      newDistribution[adjustedSlot] = Math.max(0, Math.min(100, currentVal + finalDifference)).toFixed(1);
    }

    setDistribution(newDistribution);
  };

  // Calculate dollar amount for each slot
  const getDollarAmount = (percentage) => {
    if (!manualTotal) return 0;
    return (manualTotal * (parseFloat(percentage) / 100)).toFixed(0);
  };

  // Modal style
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "98%",
    maxWidth: "98%",
    height: "90%",
    maxHeight: "90%",
    bgcolor: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column"
  };

  const handleSave = () => {
    if (!props.saveDistribution || !props.dateStr) {
      props.handleClose();
      return;
    }

    setSavingLoading(true);

    const payload = {
      date: props.dateStr,
      sales_value: manualTotal,
      distribution: timeSlots.map((slot) => ({
        qh_num: slot.startTime,
        percentage: parseFloat(distribution[slot.startTime] || 0),
      })),
    };

    props
      .saveDistribution(payload)
      .then(() => {
        props.handleClose();
      })
      .catch((err) => {
        console.error('Error saving intraday distribution', err);
      })
      .finally(() => {
        setSavingLoading(false);
      });
  };

  return (
    <Modal
      open={props.open}
      onClose={() => props.handleClose()}
      aria-labelledby="manual-intraday-distribution-modal"
    >
      <Box sx={style}>
        {/* Header Section */}
        <div className="bg-white px-6 py-5 text-slate-900 flex justify-between items-center border-b border-gray-200">
          <div>
            <h2 className="text-xl font-medium">Set Intraday Sales Distribution</h2>
            <p className="text-sm text-slate-500 mt-1">
              Distribute {manualTotal ? `$${manualTotal.toLocaleString()}` : 'manual forecast'} across half-hour windows
            </p>
          </div>
          <button
            onClick={() => props.handleClose()}
            className="text-gray-500 hover:text-gray-600 p-1 rounded-full transition-colors"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Total Percentage Bar */}
        <div className={`px-6 py-3 flex justify-between items-center border-b ${
          isValidTotal ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'
        }`}>
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-slate-700">Total:</span>
            <span className={`text-lg font-semibold ${
              isValidTotal ? 'text-emerald-700' : 'text-rose-700'
            }`}>
              {totalPercentage}%
            </span>
            {!isValidTotal && (
              <span className="text-sm text-rose-600">
                (must equal 100%)
              </span>
            )}
          </div>
          {!isValidTotal && (
            <button
              onClick={handleAutoAdjust}
              className="px-3 py-1 text-sm bg-violet-600 hover:bg-violet-700 text-white rounded-md transition-colors"
            >
              Auto-adjust to 100%
            </button>
          )}
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-x-auto overflow-y-hidden px-6 py-4">
          <div className="flex items-end justify-between w-full h-full pb-8">
            {timeSlots.map((slot) => {
              const percentage = distribution[slot.startTime] || 0;
              const dollarAmount = getDollarAmount(percentage);
              const slotWidth = `${100 / timeSlots.length}%`;

              const isEditing = editingSlot === slot.startTime;

              return (
                <div
                  key={slot.startTime}
                  className="flex flex-col items-center"
                  style={{ width: slotWidth, minWidth: '45px', maxWidth: '60px' }}
                >
                  {/* Percentage Label or Input */}
                  <div className="text-[10px] font-semibold text-violet-700 mb-1 h-4 cursor-pointer" onClick={() => handleSlotClick(slot.startTime)}>
                    {parseFloat(percentage).toFixed(1)}%
                  </div>

                  {/* Vertical Slider */}
                  <div className="bg-gray-100 rounded-md px-1 py-2 hover:bg-gray-200 transition-colors flex items-center justify-center border border-gray-300" style={{ height: '220px' }}>
                    <Slider
                      orientation="vertical"
                      value={Math.min(parseFloat(percentage), maxSliderValue)}
                      onChange={(e, newValue) => handleSliderChange(slot.startTime, newValue)}
                      min={0}
                      max={maxSliderValue}
                      step={0.1}
                      sx={{
                        color: '#6d28d9',
                        height: '200px',
                        '& .MuiSlider-thumb': {
                          width: 16,
                          height: 16,
                          '&:hover, &.Mui-focusVisible': {
                            boxShadow: '0px 0px 0px 6px rgba(109, 40, 217, 0.16)',
                          },
                        },
                        '& .MuiSlider-track': {
                          width: 6,
                        },
                        '& .MuiSlider-rail': {
                          width: 6,
                        },
                      }}
                    />
                  </div>

                  {/* Dollar Amount */}
                  <div className="text-[10px] text-slate-600 mt-1.5 text-center font-medium h-4">
                    ${parseInt(dollarAmount).toLocaleString()}
                  </div>

                  {/* Time Label */}
                  <div className="text-[10px] font-medium text-slate-700 mt-0.5 text-center">
                    {get_t_str_shortened(slot.startTime)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Edit Modal */}
        {editingSlot !== null && (
          <Modal
            open={true}
            onClose={handleEditCancel}
            aria-labelledby="edit-slot-modal"
          >
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 300,
                bgcolor: 'background.paper',
                borderRadius: '8px',
                boxShadow: 24,
                p: 3,
              }}
            >
              <h3 className="text-lg font-medium text-slate-900 mb-3">
                Edit {get_t_str(editingSlot)}
              </h3>
              <TextField
                fullWidth
                autoFocus
                label="Percentage"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleEditSave();
                  }
                }}
                type="number"
                inputProps={{
                  step: 0.1,
                  min: 0,
                  max: 100,
                }}
                helperText="Enter a value between 0 and 100"
                sx={{ mb: 2 }}
              />
              <div className="flex justify-end space-x-2">
                <button
                  onClick={handleEditCancel}
                  className="px-3 py-1.5 text-sm text-slate-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditSave}
                  className="px-3 py-1.5 text-sm text-white bg-violet-600 rounded-md hover:bg-violet-700"
                >
                  Save
                </button>
              </div>
            </Box>
          </Modal>
        )}

        {/* Instructions */}
        <div className="px-6 py-2 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
          <p className="text-xs text-slate-600">
            Click on percentage labels to edit manually, or drag the sliders to adjust distribution across the day.
          </p>
          <p className="text-xs text-slate-500">
            Scale: 0-{maxSliderValue}%
          </p>
        </div>

        {/* Footer with Save Button */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={() => props.handleClose()}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <LoadingButton
            disabled={!isValidTotal || loadingDistribution}
            loading={savingLoading}
            loadingPosition="end"
            endIcon={<SaveIcon />}
            variant="contained"
            onClick={handleSave}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              paddingX: "20px",
              paddingY: "8px",
              backgroundColor: "#6d28d9",
              "&:hover": {
                backgroundColor: "#5b21b6",
              },
              "&:disabled": {
                backgroundColor: "#e5e7eb",
              },
              fontSize: "14px",
            }}
          >
            Save Distribution
          </LoadingButton>
        </div>
      </Box>
    </Modal>
  );
}

export default ManualIntradayDistributionModal;
